import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UiHelperService } from '../../services/ui-helper.service';

export interface CcswCredenciales {
  token?: string;
  code?: string;
  state?: string;
  [key: string]: unknown;
}

const CCSW5_CHANNEL_NAME = 'ccsw5_auth_channel';
const CCSW5_AUTH_EN_CURSO_KEY = 'ccsw5AuthEnCurso';

/**
 * Popup CCSW5 + BroadcastChannel (mismo patrón que Canal de Ventas).
 */
@Injectable({ providedIn: 'root' })
export class Ccsw5AuthWindowService implements OnDestroy {
  static readonly channelName = CCSW5_CHANNEL_NAME;

  private ventanaExterna: Window | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private watcherCierre: ReturnType<typeof setInterval> | null = null;
  private timeoutCierre: ReturnType<typeof setTimeout> | null = null;
  private readonly uiHelper = inject(UiHelperService);

  private readonly credencialesSubject = new Subject<CcswCredenciales>();
  readonly credenciales$ = this.credencialesSubject.asObservable();

  private readonly ventanaCerradaSubject = new Subject<void>();
  readonly ventanaCerrada$ = this.ventanaCerradaSubject.asObservable();

  constructor(private ngZone: NgZone) {}

  abrirVentanaLoginCcsw5(): Window | null {
    const redirectUri = this.resolveRedirectUri();
    const urlDestino =
      `${environment.urlCcsw5}/auth/login` +
      `?redirect_uri=${encodeURIComponent(redirectUri)}`;
    this.detenerTodo();
    localStorage.setItem(CCSW5_AUTH_EN_CURSO_KEY, '1');

    this.broadcastChannel = new BroadcastChannel(CCSW5_CHANNEL_NAME);
    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      const data = event.data as CcswCredenciales;
      if (data?.code && data.state === '1') {
        this.cancelarTimeoutCierre();
        this.detenerTodo();
        this.ngZone.run(() => this.credencialesSubject.next(data));
      }
    };

    this.ventanaExterna = window.open(
      urlDestino,
      'ventanaCcsw5',
      'width=900,height=650,resizable=yes,scrollbars=yes'
    );
    this.iniciarWatcherCierre();
    return this.ventanaExterna;
  }

  /**
   * URI de retorno a CertiTool tras OTP.
   * - En localhost usa https (Entrust no admite http:// en redirect_uri registrados).
   * - En el resto usa `environment.ccsw5RedirectUri` o, si está vacío, el origin + path.
   */
  private resolveRedirectUri(): string {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      // Debe coincidir con la URI registrada en Entrust (solo https).
      return `https://${host}:${window.location.port || '4200'}/auth-callback`;
    }
    const configured = String(
      (environment as { ccsw5RedirectUri?: string }).ccsw5RedirectUri ?? ''
    ).trim();
    if (configured) {
      return configured;
    }
    const path = window.location.pathname || '/';
    const underCertitool = /^\/certitool(?:\/|$)/i.test(path);
    return underCertitool
      ? `${window.location.origin}/certitool/auth-callback`
      : `${window.location.origin}/auth-callback`;
  }

  estaAutenticacionEnCurso(): boolean {
    return localStorage.getItem(CCSW5_AUTH_EN_CURSO_KEY) === '1';
  }

  limpiarAutenticacionEnCurso(): void {
    localStorage.removeItem(CCSW5_AUTH_EN_CURSO_KEY);
  }

  cerrarVentana(): void {
    try {
      this.ventanaExterna?.close();
    } catch {
      /* ignore */
    }
    this.ventanaExterna = null;
  }

  ngOnDestroy(): void {
    this.detenerTodo();
  }

  private iniciarWatcherCierre(): void {
    this.watcherCierre = setInterval(() => {
      if (this.ventanaExterna && this.ventanaExterna.closed) {
        this.detenerWatcher();
        // Breve gracia por si el callback BroadcastChannel llega justo al cerrar.
        this.timeoutCierre = setTimeout(() => {
          this.timeoutCierre = null;
          this.detenerCanal();
          this.limpiarAutenticacionEnCurso();
          this.ngZone.run(() => {
            this.uiHelper.unlockUi();
            this.ventanaCerradaSubject.next();
          });
        }, 400);
      }
    }, 400);
  }

  private cancelarTimeoutCierre(): void {
    if (this.timeoutCierre) {
      clearTimeout(this.timeoutCierre);
      this.timeoutCierre = null;
    }
  }

  private detenerWatcher(): void {
    if (this.watcherCierre) {
      clearInterval(this.watcherCierre);
      this.watcherCierre = null;
    }
  }

  private detenerCanal(): void {
    try {
      this.broadcastChannel?.close();
    } catch {
      /* ignore */
    }
    this.broadcastChannel = null;
  }

  private detenerTodo(): void {
    this.cancelarTimeoutCierre();
    this.detenerWatcher();
    this.detenerCanal();
  }
}
